export type OperatorContext = "query" | "pipeline" | "accumulator" | "projection" | "update";

export interface MongoOperator {
  name: string;
  description: string;
  contexts: OperatorContext[];
  snippet?: string;
}

// ---------------------------------------------------------------------------
// Query & Logical Operators
// ---------------------------------------------------------------------------

export const QUERY_OPERATORS: MongoOperator[] = [
  // Comparison
  {
    name: "$eq",
    description: "Matches values that are equal to a specified value.",
    contexts: ["query"],
    snippet: '"$eq": ${1:value}',
  },
  {
    name: "$ne",
    description: "Matches all values that are not equal to a specified value.",
    contexts: ["query"],
    snippet: '"$ne": ${1:value}',
  },
  {
    name: "$gt",
    description: "Matches values that are greater than a specified value.",
    contexts: ["query"],
    snippet: '"$gt": ${1:value}',
  },
  {
    name: "$gte",
    description: "Matches values that are greater than or equal to a specified value.",
    contexts: ["query"],
    snippet: '"$gte": ${1:value}',
  },
  {
    name: "$lt",
    description: "Matches values that are less than a specified value.",
    contexts: ["query"],
    snippet: '"$lt": ${1:value}',
  },
  {
    name: "$lte",
    description: "Matches values that are less than or equal to a specified value.",
    contexts: ["query"],
    snippet: '"$lte": ${1:value}',
  },
  {
    name: "$in",
    description: "Matches any of the values specified in an array.",
    contexts: ["query"],
    snippet: '"$in": [${1}]',
  },
  {
    name: "$nin",
    description: "Matches none of the values specified in an array.",
    contexts: ["query"],
    snippet: '"$nin": [${1}]',
  },

  // Logical
  {
    name: "$and",
    description: "Joins query clauses with a logical AND, returns documents that match all conditions.",
    contexts: ["query"],
    snippet: '"$and": [{ ${1} }]',
  },
  {
    name: "$or",
    description: "Joins query clauses with a logical OR, returns documents that match any condition.",
    contexts: ["query"],
    snippet: '"$or": [{ ${1} }]',
  },
  {
    name: "$not",
    description: "Inverts the effect of a query expression, returns documents that do not match.",
    contexts: ["query"],
    snippet: '"$not": { ${1} }',
  },
  {
    name: "$nor",
    description: "Joins query clauses with a logical NOR, returns documents that fail all conditions.",
    contexts: ["query"],
    snippet: '"$nor": [{ ${1} }]',
  },

  // Element
  {
    name: "$exists",
    description: "Matches documents that have or do not have a specified field.",
    contexts: ["query"],
    snippet: '"$exists": ${1:true}',
  },
  {
    name: "$type",
    description: "Matches documents where the field is of the specified BSON type.",
    contexts: ["query"],
    snippet: '"$type": "${1:string}"',
  },

  // Evaluation
  {
    name: "$regex",
    description: "Selects documents where values match a specified regular expression.",
    contexts: ["query"],
    snippet: '"$regex": "${1:pattern}"',
  },
  {
    name: "$options",
    description: "Specifies options for a $regex expression (i, m, x, s).",
    contexts: ["query"],
    snippet: '"$options": "${1:i}"',
  },
  {
    name: "$text",
    description: "Performs text search on fields with a text index.",
    contexts: ["query"],
    snippet: '"$text": { "$search": "${1}" }',
  },
  {
    name: "$search",
    description: "Specifies the search string for a $text query.",
    contexts: ["query"],
    snippet: '"$search": "${1}"',
  },
  {
    name: "$where",
    description: "Matches documents that satisfy a JavaScript expression.",
    contexts: ["query"],
    snippet: '"$where": "${1:this.field > 0}"',
  },
  {
    name: "$mod",
    description: "Performs a modulo operation on the value of a field and selects documents with a specified result.",
    contexts: ["query"],
    snippet: '"$mod": [${1:divisor}, ${2:remainder}]',
  },
  {
    name: "$expr",
    description: "Allows use of aggregation expressions within the query language.",
    contexts: ["query"],
    snippet: '"$expr": { ${1} }',
  },
  {
    name: "$jsonSchema",
    description: "Validates documents against the given JSON Schema.",
    contexts: ["query"],
    snippet: '"$jsonSchema": { ${1} }',
  },

  // Array
  {
    name: "$all",
    description: "Matches arrays that contain all elements specified in the query.",
    contexts: ["query"],
    snippet: '"$all": [${1}]',
  },
  {
    name: "$elemMatch",
    description: "Matches documents that contain an array element matching all specified conditions.",
    contexts: ["query", "projection"],
    snippet: '"$elemMatch": { ${1} }',
  },
  {
    name: "$size",
    description: "Matches arrays with the specified number of elements.",
    contexts: ["query"],
    snippet: '"$size": ${1:number}',
  },

  // Bitwise
  {
    name: "$bitsAllClear",
    description: "Matches where all bit positions given by the query are clear (0).",
    contexts: ["query"],
    snippet: '"$bitsAllClear": ${1:bitmask}',
  },
  {
    name: "$bitsAllSet",
    description: "Matches where all bit positions given by the query are set (1).",
    contexts: ["query"],
    snippet: '"$bitsAllSet": ${1:bitmask}',
  },
  {
    name: "$bitsAnyClear",
    description: "Matches where any bit position given by the query is clear (0).",
    contexts: ["query"],
    snippet: '"$bitsAnyClear": ${1:bitmask}',
  },
  {
    name: "$bitsAnySet",
    description: "Matches where any bit position given by the query is set (1).",
    contexts: ["query"],
    snippet: '"$bitsAnySet": ${1:bitmask}',
  },

  // Geospatial
  {
    name: "$geoWithin",
    description: "Selects geometries within a bounding GeoJSON geometry.",
    contexts: ["query"],
    snippet: '"$geoWithin": { "$geometry": { "type": "${1:Polygon}", "coordinates": [${2}] } }',
  },
  {
    name: "$geoIntersects",
    description: "Selects geometries that intersect with a GeoJSON geometry.",
    contexts: ["query"],
    snippet: '"$geoIntersects": { "$geometry": { "type": "${1:Point}", "coordinates": [${2}] } }',
  },
  {
    name: "$near",
    description: "Returns geospatial objects in proximity to a point, sorted by distance.",
    contexts: ["query"],
    snippet: '"$near": { "$geometry": { "type": "Point", "coordinates": [${1}] }, "$maxDistance": ${2} }',
  },
  {
    name: "$nearSphere",
    description: "Returns geospatial objects in proximity to a point on a sphere, sorted by distance.",
    contexts: ["query"],
    snippet: '"$nearSphere": { "$geometry": { "type": "Point", "coordinates": [${1}] }, "$maxDistance": ${2} }',
  },
];

// ---------------------------------------------------------------------------
// Aggregation Pipeline Stages
// ---------------------------------------------------------------------------

export const PIPELINE_STAGES: MongoOperator[] = [
  {
    name: "$addFields",
    description: "Adds new fields to documents. Outputs documents with all existing fields and the newly added fields.",
    contexts: ["pipeline"],
    snippet: '{ "$addFields": { "${1:field}": ${2:expression} } }',
  },
  {
    name: "$bucket",
    description: "Categorizes incoming documents into groups (buckets) based on a specified expression and bucket boundaries.",
    contexts: ["pipeline"],
    snippet: '{ "$bucket": { "groupBy": "$${1:field}", "boundaries": [${2}], "default": "${3:Other}" } }',
  },
  {
    name: "$bucketAuto",
    description: "Categorizes incoming documents into a specified number of groups (buckets) based on a specified expression.",
    contexts: ["pipeline"],
    snippet: '{ "$bucketAuto": { "groupBy": "$${1:field}", "buckets": ${2:5} } }',
  },
  {
    name: "$changeStream",
    description: "Returns a Change Stream cursor on a collection, database, or deployment.",
    contexts: ["pipeline"],
    snippet: '{ "$changeStream": { ${1} } }',
  },
  {
    name: "$collStats",
    description: "Returns statistics regarding a collection or view.",
    contexts: ["pipeline"],
    snippet: '{ "$collStats": { "storageStats": {} } }',
  },
  {
    name: "$count",
    description: "Returns a count of the number of documents at this stage of the aggregation pipeline.",
    contexts: ["pipeline"],
    snippet: '{ "$count": "${1:totalCount}" }',
  },
  {
    name: "$densify",
    description: "Creates new documents in a sequence of documents where certain values in a field are missing.",
    contexts: ["pipeline"],
    snippet: '{ "$densify": { "field": "${1:field}", "range": { "step": ${2:1}, "bounds": "${3:full}" } } }',
  },
  {
    name: "$documents",
    description: "Returns literal documents from input expressions. Used in $lookup pipeline and db.aggregate().",
    contexts: ["pipeline"],
    snippet: '{ "$documents": [{ ${1} }] }',
  },
  {
    name: "$facet",
    description: "Processes multiple aggregation pipelines within a single stage on the same set of input documents.",
    contexts: ["pipeline"],
    snippet: '{ "$facet": { "${1:output}": [${2}] } }',
  },
  {
    name: "$fill",
    description: "Populates null and missing field values within documents.",
    contexts: ["pipeline"],
    snippet: '{ "$fill": { "output": { "${1:field}": { "method": "${2:linear}" } } } }',
  },
  {
    name: "$geoNear",
    description: "Returns documents ordered by proximity to a geospatial point. Must be the first stage.",
    contexts: ["pipeline"],
    snippet: '{ "$geoNear": { "near": { "type": "Point", "coordinates": [${1}] }, "distanceField": "${2:distance}" } }',
  },
  {
    name: "$graphLookup",
    description: "Performs a recursive search on a collection, adding a depth field to each traversal.",
    contexts: ["pipeline"],
    snippet: '{ "$graphLookup": { "from": "${1:collection}", "startWith": "$${2:field}", "connectFromField": "${3:from}", "connectToField": "${4:to}", "as": "${5:result}" } }',
  },
  {
    name: "$group",
    description: "Groups input documents by a specified _id expression and applies accumulator expressions.",
    contexts: ["pipeline"],
    snippet: '{ "$group": { "_id": "$${1:field}", "${2:total}": { "$sum": ${3:1} } } }',
  },
  {
    name: "$indexStats",
    description: "Returns statistics regarding the use of each index for the collection.",
    contexts: ["pipeline"],
    snippet: '{ "$indexStats": {} }',
  },
  {
    name: "$limit",
    description: "Limits the number of documents passed to the next stage.",
    contexts: ["pipeline"],
    snippet: '{ "$limit": ${1:10} }',
  },
  {
    name: "$listSessions",
    description: "Lists all active sessions that have been idle long enough to have propagated to the system.sessions collection.",
    contexts: ["pipeline"],
    snippet: '{ "$listSessions": { ${1} } }',
  },
  {
    name: "$lookup",
    description: "Performs a left outer join to another collection in the same database.",
    contexts: ["pipeline"],
    snippet: '{ "$lookup": { "from": "${1:collection}", "localField": "${2:localField}", "foreignField": "${3:foreignField}", "as": "${4:result}" } }',
  },
  {
    name: "$match",
    description: "Filters documents to pass only those that match the specified condition(s).",
    contexts: ["pipeline"],
    snippet: '{ "$match": { ${1} } }',
  },
  {
    name: "$merge",
    description: "Writes the results of the aggregation pipeline to a specified collection.",
    contexts: ["pipeline"],
    snippet: '{ "$merge": { "into": "${1:collection}" } }',
  },
  {
    name: "$out",
    description: "Writes the results of the aggregation pipeline to a collection. Must be the last stage.",
    contexts: ["pipeline"],
    snippet: '{ "$out": "${1:collection}" }',
  },
  {
    name: "$planCacheStats",
    description: "Returns plan cache information for a collection.",
    contexts: ["pipeline"],
    snippet: '{ "$planCacheStats": {} }',
  },
  {
    name: "$project",
    description: "Reshapes each document, including, excluding, or adding fields.",
    contexts: ["pipeline"],
    snippet: '{ "$project": { "${1:field}": 1 } }',
  },
  {
    name: "$redact",
    description: "Restricts the content of documents based on information stored in the documents themselves.",
    contexts: ["pipeline"],
    snippet: '{ "$redact": { "$cond": { "if": { ${1} }, "then": "$$DESCEND", "else": "$$PRUNE" } } }',
  },
  {
    name: "$replaceRoot",
    description: "Replaces the input document with the specified document.",
    contexts: ["pipeline"],
    snippet: '{ "$replaceRoot": { "newRoot": "$${1:field}" } }',
  },
  {
    name: "$replaceWith",
    description: "Replaces the input document with the specified document. Alias for $replaceRoot.",
    contexts: ["pipeline"],
    snippet: '{ "$replaceWith": "$${1:field}" }',
  },
  {
    name: "$sample",
    description: "Randomly selects the specified number of documents from its input.",
    contexts: ["pipeline"],
    snippet: '{ "$sample": { "size": ${1:10} } }',
  },
  {
    name: "$search",
    description: "Performs full-text search using Atlas Search on string fields.",
    contexts: ["pipeline"],
    snippet: '{ "$search": { "index": "${1:default}", "text": { "query": "${2}", "path": "${3:field}" } } }',
  },
  {
    name: "$searchMeta",
    description: "Returns metadata results for an Atlas Search query.",
    contexts: ["pipeline"],
    snippet: '{ "$searchMeta": { "index": "${1:default}", "facet": { ${2} } } }',
  },
  {
    name: "$set",
    description: "Adds new fields to documents. Alias for $addFields.",
    contexts: ["pipeline"],
    snippet: '{ "$set": { "${1:field}": ${2:expression} } }',
  },
  {
    name: "$setWindowFields",
    description: "Performs operations on a specified span of documents in a collection (window functions).",
    contexts: ["pipeline"],
    snippet: '{ "$setWindowFields": { "sortBy": { "${1:field}": 1 }, "output": { "${2:result}": { ${3} } } } }',
  },
  {
    name: "$skip",
    description: "Skips the first n documents and passes the remaining documents to the next stage.",
    contexts: ["pipeline"],
    snippet: '{ "$skip": ${1:0} }',
  },
  {
    name: "$sort",
    description: "Reorders the document stream by a specified sort key.",
    contexts: ["pipeline"],
    snippet: '{ "$sort": { "${1:field}": ${2:1} } }',
  },
  {
    name: "$sortByCount",
    description: "Groups incoming documents based on the value of a specified expression, then sorts by count.",
    contexts: ["pipeline"],
    snippet: '{ "$sortByCount": "$${1:field}" }',
  },
  {
    name: "$unionWith",
    description: "Performs a union of two collections, combining pipeline results from two collections into a single result set.",
    contexts: ["pipeline"],
    snippet: '{ "$unionWith": { "coll": "${1:collection}", "pipeline": [${2}] } }',
  },
  {
    name: "$unset",
    description: "Removes specified fields from documents. Alias for $project with exclusion.",
    contexts: ["pipeline"],
    snippet: '{ "$unset": "${1:field}" }',
  },
  {
    name: "$unwind",
    description: "Deconstructs an array field from the input documents to output a document for each element.",
    contexts: ["pipeline"],
    snippet: '{ "$unwind": "$${1:field}" }',
  },
];

// ---------------------------------------------------------------------------
// Accumulator Operators (used in $group, $setWindowFields, etc.)
// ---------------------------------------------------------------------------

export const ACCUMULATOR_OPERATORS: MongoOperator[] = [
  {
    name: "$sum",
    description: "Calculates and returns the collective sum of numeric values.",
    contexts: ["accumulator"],
    snippet: '"$sum": ${1:"$field"}',
  },
  {
    name: "$avg",
    description: "Returns the average value of numeric values.",
    contexts: ["accumulator"],
    snippet: '"$avg": "$${1:field}"',
  },
  {
    name: "$first",
    description: "Returns the value from the first document for each group.",
    contexts: ["accumulator"],
    snippet: '"$first": "$${1:field}"',
  },
  {
    name: "$last",
    description: "Returns the value from the last document for each group.",
    contexts: ["accumulator"],
    snippet: '"$last": "$${1:field}"',
  },
  {
    name: "$min",
    description: "Returns the minimum value from all documents in each group.",
    contexts: ["accumulator"],
    snippet: '"$min": "$${1:field}"',
  },
  {
    name: "$max",
    description: "Returns the maximum value from all documents in each group.",
    contexts: ["accumulator"],
    snippet: '"$max": "$${1:field}"',
  },
  {
    name: "$push",
    description: "Returns an array of all values that result from applying an expression to each document in a group.",
    contexts: ["accumulator"],
    snippet: '"$push": "$${1:field}"',
  },
  {
    name: "$addToSet",
    description: "Returns an array of all unique values that result from applying an expression to each document in a group.",
    contexts: ["accumulator"],
    snippet: '"$addToSet": "$${1:field}"',
  },
  {
    name: "$stdDevPop",
    description: "Calculates the population standard deviation of the input values.",
    contexts: ["accumulator"],
    snippet: '"$stdDevPop": "$${1:field}"',
  },
  {
    name: "$stdDevSamp",
    description: "Calculates the sample standard deviation of the input values.",
    contexts: ["accumulator"],
    snippet: '"$stdDevSamp": "$${1:field}"',
  },
  {
    name: "$mergeObjects",
    description: "Combines multiple documents into a single document.",
    contexts: ["accumulator"],
    snippet: '"$mergeObjects": "$${1:field}"',
  },
  {
    name: "$accumulator",
    description: "Defines a custom accumulator using JavaScript init, accumulate, and merge functions.",
    contexts: ["accumulator"],
    snippet: '"$accumulator": { "init": "${1:function() { return {}; }}", "accumulate": "${2:function(state, value) { return state; }}", "accumulateArgs": ["$${3:field}"], "merge": "${4:function(s1, s2) { return s1; }}" }',
  },
  {
    name: "$count",
    description: "Returns the number of documents in a group. Distinct from the $count pipeline stage.",
    contexts: ["accumulator"],
    snippet: '"$count": {}',
  },
  {
    name: "$top",
    description: "Returns the top element within a group according to the specified sort order.",
    contexts: ["accumulator"],
    snippet: '"$top": { "sortBy": { "${1:field}": 1 }, "output": "$${2:field}" }',
  },
  {
    name: "$topN",
    description: "Returns an aggregation of the top n elements within a group, according to the specified sort order.",
    contexts: ["accumulator"],
    snippet: '"$topN": { "n": ${1:5}, "sortBy": { "${2:field}": 1 }, "output": "$${3:field}" }',
  },
  {
    name: "$bottom",
    description: "Returns the bottom element within a group according to the specified sort order.",
    contexts: ["accumulator"],
    snippet: '"$bottom": { "sortBy": { "${1:field}": 1 }, "output": "$${2:field}" }',
  },
  {
    name: "$bottomN",
    description: "Returns an aggregation of the bottom n elements within a group, according to the specified sort order.",
    contexts: ["accumulator"],
    snippet: '"$bottomN": { "n": ${1:5}, "sortBy": { "${2:field}": 1 }, "output": "$${3:field}" }',
  },
  {
    name: "$firstN",
    description: "Returns an aggregation of the first n elements within a group. Order is defined only with a preceding $sort.",
    contexts: ["accumulator"],
    snippet: '"$firstN": { "n": ${1:5}, "input": "$${2:field}" }',
  },
  {
    name: "$lastN",
    description: "Returns an aggregation of the last n elements within a group. Order is defined only with a preceding $sort.",
    contexts: ["accumulator"],
    snippet: '"$lastN": { "n": ${1:5}, "input": "$${2:field}" }',
  },
  {
    name: "$maxN",
    description: "Returns an aggregation of the n maximum valued elements in a group.",
    contexts: ["accumulator"],
    snippet: '"$maxN": { "n": ${1:5}, "input": "$${2:field}" }',
  },
  {
    name: "$minN",
    description: "Returns an aggregation of the n minimum valued elements in a group.",
    contexts: ["accumulator"],
    snippet: '"$minN": { "n": ${1:5}, "input": "$${2:field}" }',
  },
];

// ---------------------------------------------------------------------------
// Update Operators
// ---------------------------------------------------------------------------

export const UPDATE_OPERATORS: MongoOperator[] = [
  // Field update operators
  {
    name: "$set",
    description: "Sets the value of a field in a document.",
    contexts: ["update"],
    snippet: '"$set": { "${1:field}": ${2:value} }',
  },
  {
    name: "$unset",
    description: "Removes the specified field from a document.",
    contexts: ["update"],
    snippet: '"$unset": { "${1:field}": "" }',
  },
  {
    name: "$inc",
    description: "Increments the value of the field by the specified amount.",
    contexts: ["update"],
    snippet: '"$inc": { "${1:field}": ${2:1} }',
  },
  {
    name: "$mul",
    description: "Multiplies the value of the field by the specified amount.",
    contexts: ["update"],
    snippet: '"$mul": { "${1:field}": ${2:number} }',
  },
  {
    name: "$rename",
    description: "Renames a field.",
    contexts: ["update"],
    snippet: '"$rename": { "${1:oldName}": "${2:newName}" }',
  },
  {
    name: "$min",
    description: "Updates the field only if the specified value is less than the existing field value.",
    contexts: ["update"],
    snippet: '"$min": { "${1:field}": ${2:value} }',
  },
  {
    name: "$max",
    description: "Updates the field only if the specified value is greater than the existing field value.",
    contexts: ["update"],
    snippet: '"$max": { "${1:field}": ${2:value} }',
  },
  {
    name: "$currentDate",
    description: "Sets the value of a field to the current date, either as a Date or a Timestamp.",
    contexts: ["update"],
    snippet: '"$currentDate": { "${1:field}": true }',
  },

  // Array update operators
  {
    name: "$addToSet",
    description: "Adds elements to an array only if they do not already exist in the set.",
    contexts: ["update"],
    snippet: '"$addToSet": { "${1:field}": ${2:value} }',
  },
  {
    name: "$pop",
    description: "Removes the first or last item of an array. Pass -1 for first, 1 for last.",
    contexts: ["update"],
    snippet: '"$pop": { "${1:field}": ${2:1} }',
  },
  {
    name: "$pull",
    description: "Removes all array elements that match a specified query.",
    contexts: ["update"],
    snippet: '"$pull": { "${1:field}": ${2:value} }',
  },
  {
    name: "$push",
    description: "Appends a specified value to an array.",
    contexts: ["update"],
    snippet: '"$push": { "${1:field}": ${2:value} }',
  },
  {
    name: "$pullAll",
    description: "Removes all matching values from an array.",
    contexts: ["update"],
    snippet: '"$pullAll": { "${1:field}": [${2}] }',
  },

  // Array update modifiers (used with $push / $addToSet)
  {
    name: "$each",
    description: "Modifies $push and $addToSet to append multiple items for an array update.",
    contexts: ["update"],
    snippet: '"$each": [${1}]',
  },
  {
    name: "$position",
    description: "Modifies $push to specify the position in the array to add elements.",
    contexts: ["update"],
    snippet: '"$position": ${1:0}',
  },
  {
    name: "$slice",
    description: "Modifies $push to limit the size of updated arrays.",
    contexts: ["update"],
    snippet: '"$slice": ${1:number}',
  },
  {
    name: "$sort",
    description: "Modifies $push to reorder documents stored in an array.",
    contexts: ["update"],
    snippet: '"$sort": { "${1:field}": ${2:1} }',
  },
];

// ---------------------------------------------------------------------------
// Merged lookup map
// ---------------------------------------------------------------------------

function buildOperatorMap(): Map<string, MongoOperator> {
  const map = new Map<string, MongoOperator>();

  const mergeInto = (operators: MongoOperator[]) => {
    for (const op of operators) {
      const existing = map.get(op.name);
      if (existing) {
        // Merge contexts for operators that appear in multiple categories
        const mergedContexts = new Set([...existing.contexts, ...op.contexts]);
        map.set(op.name, {
          ...existing,
          contexts: [...mergedContexts],
        });
      } else {
        map.set(op.name, { ...op });
      }
    }
  };

  mergeInto(QUERY_OPERATORS);
  mergeInto(PIPELINE_STAGES);
  mergeInto(ACCUMULATOR_OPERATORS);
  mergeInto(UPDATE_OPERATORS);

  return map;
}

export const ALL_OPERATORS: Map<string, MongoOperator> = buildOperatorMap();
